// src/components/AttendanceRecord.js
import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Edit, Calendar } from 'lucide-react';
import * as dateUtils from '../utils/dateUtils';

function AttendanceRecord({ userInfo }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [records, setRecords] = useState([]);
  const [shifts, setShifts] = useState({});
  const [message, setMessage] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editData, setEditData] = useState({
    clock_in: '',
    clock_out: '',
    note: '',
    modification_reason: '',
    break_time: '1',
    irregularType: 'absence',
    reportedTo: ''
  });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // シフトデータを取得
  const fetchShifts = async (yearMonth) => {
    try {
      console.log('Fetching shifts for:', yearMonth);
      const response = await fetch(`/api/shifts/${yearMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Shifts error response:', text);
        throw new Error('シフトデータの取得に失敗しました');
      }

      const data = await response.json();
      console.log('Received shifts data:', data);

      const formattedShifts = data.reduce((acc, shift) => {
        const dateObj = new Date(shift.date);
        const dateStr = dateObj.toISOString().split('T')[0];

        // 時間文字列を適切にフォーマット
        let startTime = '';
        if (shift.start_time) {
          // HH:MM:SS形式の時間文字列をDate型に変換してフォーマット
          const [hours, minutes] = shift.start_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(hours, minutes, 0);
          startTime = timeDate.toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        let endTime = '';
        if (shift.end_time) {
          // HH:MM:SS形式の時間文字列をDate型に変換してフォーマット
          const [hours, minutes] = shift.end_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(hours, minutes, 0);
          endTime = timeDate.toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        acc[dateStr] = {
          startTime: startTime,
          endTime: endTime
        };
        return acc;
      }, {});
      
      console.log('Formatted shifts:', formattedShifts);
      setShifts(formattedShifts);
    } catch (error) {
      console.error('シフトデータの取得エラー:', error);
    }
  };

  // 勤怠記録を取得
  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      const yearMonth = selectedMonth;
      console.log('選択された月:', yearMonth);

      const response = await fetch(`/api/time-records?month=${yearMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('勤怠記録の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得した勤怠記録:', data);

      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(record => {
        if (!record || !record.recorded_at) return;
        
        // dateUtils.jsを使用して日付を変換
        const dateStr = dateUtils.formatDateToJST(record.recorded_at);
        
        console.log(`レコード: ID=${record.id}, UTC日時=${record.recorded_at}, JST日付=${dateStr}, タイプ=${record.record_type}`);
        
        if (!formattedData[dateStr]) {
          formattedData[dateStr] = {
            date: dateStr,
            records: []
          };
        }
        
        formattedData[dateStr].records.push(record);
      });
      
      // 各日付の出勤・退勤時間を特定
      Object.keys(formattedData).forEach(dateStr => {
        const dayRecords = formattedData[dateStr].records;
        let clockIn = null;
        let clockOut = null;
        let note = '';
        let absence_reason = null;
        let break_time = '1:00';
        
        console.log(`日付 ${dateStr} の処理開始 - レコード数: ${dayRecords.length}`);
        console.log(`日付 ${dateStr} の全レコード:`, dayRecords);
        
        // 通常の打刻記録があるかどうかを確認
        const hasNormalRecords = dayRecords.some(record => 
          record.record_type === 'clock_in' || record.record_type === 'in' ||
          record.record_type === 'clock_out' || record.record_type === 'out'
        );
        
        console.log(`日付 ${dateStr} - 通常の打刻記録あり: ${hasNormalRecords}`);
        
        // 欠勤記録を日付でソートして最新のものを取得（通常の打刻記録がない場合のみ）
        if (!hasNormalRecords) {
          const absenceRecords = dayRecords.filter(record => record.record_type === 'absence');
          
          if (absenceRecords.length > 0) {
            // created_atで降順ソート（最新のものが先頭に）
            const sortedAbsenceRecords = absenceRecords.sort((a, b) => {
              return new Date(b.created_at) - new Date(a.created_at);
            });
            
            console.log(`日付 ${dateStr} の欠勤記録:`, sortedAbsenceRecords);
            
            // 最新の欠勤記録を使用
            const latestAbsence = sortedAbsenceRecords[0];
            absence_reason = latestAbsence.absence_reason;
            note = latestAbsence.note || '';
            
            // デバッグ情報
            console.log(`日付 ${dateStr} の最新欠勤記録:`, {
              id: latestAbsence.id,
              created_at: latestAbsence.created_at,
              absence_reason: absence_reason
            });
          }
        }
        
        // 出退勤記録の処理
        const inRecords = dayRecords.filter(record => 
          record.record_type === 'clock_in' || record.record_type === 'in'
        );
        const outRecords = dayRecords.filter(record => 
          record.record_type === 'clock_out' || record.record_type === 'out'
        );
        
        console.log(`日付 ${dateStr} の出勤記録:`, inRecords.length, '件');
        console.log(`日付 ${dateStr} の退勤記録:`, outRecords.length, '件');
        
        // 出勤記録の処理 - 最も早い時間を選択
        if (inRecords.length > 0) {
          // 時間でソート（昇順）
          const sortedInRecords = [...inRecords].sort((a, b) => {
            const timeA = new Date(a.recorded_at).getTime();
            const timeB = new Date(b.recorded_at).getTime();
            return timeA - timeB;  // 昇順（早い時間が先）
          });
          
          // 各記録の時間を表示
          sortedInRecords.forEach((record, index) => {
            const jstTime = dateUtils.formatTimeToJST(record.recorded_at);
            console.log(`出勤記録[${index}]: ID=${record.id}, UTC=${record.recorded_at}, JST=${jstTime}`);
          });
          
          // 最も早い出勤記録を使用
          const earliestRecord = sortedInRecords[0];
          clockIn = dateUtils.formatTimeToJST(earliestRecord.recorded_at);
          
          console.log(`日付 ${dateStr} - 最も早い出勤記録を選択:`, {
            id: earliestRecord.id,
            recorded_at: earliestRecord.recorded_at,
            jst_time: clockIn
          });
        }
        
        // 退勤記録の処理 - 最も遅い時間を選択
        if (outRecords.length > 0) {
          // 時間でソート（降順）
          const sortedOutRecords = [...outRecords].sort((a, b) => {
            const timeA = new Date(a.recorded_at).getTime();
            const timeB = new Date(b.recorded_at).getTime();
            return timeB - timeA;  // 降順（遅い時間が先）
          });
          
          // 各記録の時間を表示
          sortedOutRecords.forEach((record, index) => {
            const jstTime = dateUtils.formatTimeToJST(record.recorded_at);
            console.log(`退勤記録[${index}]: ID=${record.id}, UTC=${record.recorded_at}, JST=${jstTime}`);
          });
          
          // 最も遅い退勤記録を使用
          const latestRecord = sortedOutRecords[0];
          clockOut = dateUtils.formatTimeToJST(latestRecord.recorded_at);
          
          console.log(`日付 ${dateStr} - 最も遅い退勤記録を選択:`, {
            id: latestRecord.id,
            recorded_at: latestRecord.recorded_at,
            jst_time: clockOut
          });
        }
        
        // 欠勤記録がない場合のみ、通常の打刻のnoteを使用
        if (!absence_reason) {
          const noteRecord = [...inRecords, ...outRecords].find(record => record.note);
          if (noteRecord) {
            note = noteRecord.note;
          }
        }
        
        // 休憩時間の処理
        const breakRecord = [...inRecords, ...outRecords].find(record => record.break_time);
        if (breakRecord) {
          break_time = `${Math.floor(breakRecord.break_time / 60)}:${breakRecord.break_time % 60 < 10 ? '0' + (breakRecord.break_time % 60) : breakRecord.break_time % 60}`;
        }
        
        formattedData[dateStr].clock_in = clockIn;
        formattedData[dateStr].clock_out = clockOut;
        formattedData[dateStr].note = note;
        formattedData[dateStr].absence_reason = absence_reason;
        formattedData[dateStr].break_time = break_time;
        
        console.log(`日付 ${dateStr} の最終結果:`, {
          clock_in: clockIn,
          clock_out: clockOut,
          note,
          absence_reason,
          break_time
        });
      });
      
      // 日付でソート
      const sortedRecords = Object.values(formattedData).sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });
      
      console.log('整形した勤怠記録:', sortedRecords);
      
      setRecords(sortedRecords);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching attendance records:', err);
      setMessage('勤怠記録の取得に失敗しました: ' + err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchAttendanceRecords(),
          fetchShifts(selectedMonth)
        ]);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setMessage('データの取得に失敗しました');
      }
    };

    fetchData();

    // 定期的な更新を設定（1分ごと）
    const intervalId = setInterval(fetchData, 60000);

    return () => clearInterval(intervalId);
  }, [selectedMonth]);

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // 月の日数を取得する関数
  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  // 日付の配列を生成する関数
  const generateDatesForMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const paddedDay = day.toString().padStart(2, '0');
      const dateStr = `${yearMonth}-${paddedDay}`;
      dates.push(dateStr);
    }

    return dates;
  };

  // 修正ボタンのハンドラー
  const handleEditClick = (record, dateStr) => {
    console.log('修正ボタンがクリックされました:', { record, dateStr });
    
    // 日付から対象の記録を特定
    const recordsForDate = records.find(r => r.date === dateStr);
    console.log('対象日の記録:', recordsForDate);
    
    // レコードが存在しない場合は新規作成モードとして扱う
    const isNewRecord = !recordsForDate || (!recordsForDate.clock_in && !recordsForDate.clock_out);
    console.log('新規作成モード:', isNewRecord);
    
    // 最初のレコードのIDを使用、または新規作成の場合は 'new'
    const recordId = isNewRecord ? 'new' : (recordsForDate.id || 'new');
    console.log('選択されたレコードID:', recordId);
    
    // 使用するレコードデータ（存在しない場合は空のオブジェクト）
    const recordData = recordsForDate || { date: dateStr };
    
    setSelectedRecord({
      id: recordId,
      date: dateStr,
      ...recordData
    });
    
    // 時間をHTML time input形式（HH:MM）に変換
    let clockInTime = '';
    if (recordData.clock_in) {
      // 日本時間表記から時間と分を抽出
      const timeParts = recordData.clock_in.split(':');
      if (timeParts.length >= 2) {
        clockInTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }
    
    let clockOutTime = '';
    if (recordData.clock_out) {
      // 日本時間表記から時間と分を抽出
      const timeParts = recordData.clock_out.split(':');
      if (timeParts.length >= 2) {
        clockOutTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }
    
    setEditData({
      clock_in: clockInTime,
      clock_out: clockOutTime,
      note: recordData.note || '',
      modification_reason: '',
      break_time: recordData.break_time ? recordData.break_time.split(':')[0] : '1',
      irregularType: 'absence',
      reportedTo: ''
    });
    
    console.log('編集モーダルを開きます');
    setIsEditModalOpen(true);
  };

  // 欠勤ボタンのハンドラー → イレギュラーボタンのハンドラーに変更
  const handleIrregularClick = (dateStr) => {
    setSelectedRecord({ date: dateStr });
    setEditData({
      ...editData,
      note: '',
      irregularType: 'absence',
      reportedTo: ''
    });
    setIsAbsenceModalOpen(true);
  };

  // 修正を保存
  const handleSaveEdit = async () => {
    console.log('修正保存ボタンがクリックされました');
    
    if (!selectedRecord?.date) {
      console.error('修正対象の記録が見つかりません');
      setMessage('修正対象の記録が見つかりません');
      return;
    }
    
    if (!editData.modification_reason) {
      console.error('修正理由が入力されていません');
      setMessage('修正理由を入力してください');
      return;
    }

    // 入力チェック - 出勤時間または退勤時間のいずれかは必須
    if (!editData.clock_in && !editData.clock_out) {
      console.error('出勤時間または退勤時間を入力してください');
      setMessage('出勤時間または退勤時間を入力してください');
      return;
    }

    try {
      console.log('送信する修正データ:', {
        selectedRecord,
        editData
      });
      
      // 対象日の最初のレコードIDを使用
      const recordId = selectedRecord.id || 'new';
      console.log('使用するレコードID:', recordId);
      
      // 時間フォーマットを確認
      let clockIn = editData.clock_in;
      let clockOut = editData.clock_out;
      
      // 秒が含まれていない場合は追加
      if (clockIn && clockIn.split(':').length === 2) {
        clockIn = `${clockIn}:00`;
      }
      
      if (clockOut && clockOut.split(':').length === 2) {
        clockOut = `${clockOut}:00`;
      }
      
      console.log('フォーマット後の時間:', { clockIn, clockOut });
      
      const requestBody = {
        date: selectedRecord.date,
        clock_in: clockIn,
        clock_out: clockOut,
        note: editData.note,
        modification_reason: editData.modification_reason,
        break_time: editData.break_time
      };
      
      console.log('リクエスト先URL:', `/api/time-records/${recordId}`);
      console.log('リクエストボディ:', requestBody);
      
      const response = await fetch(`/api/time-records/${recordId}`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('レスポンスステータス:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('エラーレスポンス:', errorData);
        throw new Error(errorData.message || '修正の保存に失敗しました');
      }

      const result = await response.json();
      console.log('修正結果:', result);
      
      setIsEditModalOpen(false);
      await fetchAttendanceRecords();
      setMessage('勤怠記録を更新しました');

    } catch (error) {
      console.error('修正保存エラー:', error);
      setMessage(error.message);
    }
  };

  // 特定のフィールドを削除するハンドラー
  const handleDeleteField = async (recordId, field) => {
    try {
      if (!recordId) {
        setMessage('削除対象の記録が見つかりません');
        return;
      }

      console.log(`${field}を削除します:`, recordId);

      // APIリクエスト
      const url = `/api/time-records/${recordId}/field`;
      console.log('削除リクエスト先URL:', url);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ field })
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        // エラーレスポンスの処理を改善
        let errorMessage = '';
        try {
          const errorData = await response.json();
          console.log('削除エラーレスポンス:', errorData);
          errorMessage = errorData.message || `${field}の削除に失敗しました`;
        } catch (jsonError) {
          // JSONとして解析できない場合はテキストとして取得
          const errorText = await response.text();
          console.log('削除エラーレスポンス (テキスト):', errorText);
          errorMessage = `${field}の削除に失敗しました: サーバーエラー`;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      setMessage(`${field === 'clock_in' ? '出勤時間' : field === 'clock_out' ? '退勤時間' : field === 'break_time' ? '休憩時間' : '備考'}を削除しました`);
      fetchAttendanceRecords(); // 勤怠記録を再取得
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage(`${field}の削除に失敗しました: ` + err.message);
    }
  };

  // 欠勤を保存 → イレギュラーを保存
  const handleSaveAbsence = async () => {
    try {
      // 備考欄に表示する内容を整形
      const irregularTypeText = {
        'late': '遅刻',
        'early': '早退',
        'absence': '欠勤'
      }[editData.irregularType];
      
      // 報告先をそのまま使用（ユーザーが入力した通りに表示）
      const reportedTo = editData.reportedTo ? editData.reportedTo.trim() : '';
      // 「報告先:」という文字列を追加しない
      const formattedReportedTo = reportedTo ? ` - ${reportedTo}` : '';
      
      const formattedNote = `${irregularTypeText}: ${editData.note}${formattedReportedTo}`;
      
      const response = await fetch('/api/time-records/absence', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: selectedRecord.date,
          reason: formattedNote,
          irregularType: editData.irregularType
        })
      });

      if (!response.ok) throw new Error('イレギュラーの登録に失敗しました');

      setIsAbsenceModalOpen(false);
      await fetchAttendanceRecords();
    } catch (error) {
      console.error('イレギュラー登録エラー:', error);
      setMessage('イレギュラーの登録に失敗しました');
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="border rounded px-3 py-2"
        />
      </div>

      {message && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {message}
        </div>
      )}

      {/* デスクトップ表示 */}
      {!isMobile && (
        <table className="min-w-full bg-white border">
          <thead>
            <tr>
              <th className="px-4 py-2">日付</th>
              <th className="px-4 py-2">予定</th>
              <th className="px-4 py-2">出勤</th>
              <th className="px-4 py-2">退勤</th>
              <th className="px-4 py-2">休憩時間</th>
              <th className="px-4 py-2">備考</th>
              <th className="px-4 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
              const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
              const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
              const shift = shifts[dateStr] || { startTime: '', endTime: '' };
              const record = records.find(r => r.date === dateStr) || {};
              
              // 出勤・退勤レコードのIDを取得
              const recordsForDate = record.records || [];
              const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
              const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
              const inRecordId = inRecord ? inRecord.id : null;
              const outRecordId = outRecord ? outRecord.id : null;
              
              // 休憩時間と備考のレコードIDを特定
              // 休憩時間は退勤レコードに保存されることが多いため、まず退勤レコードを確認
              const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
              const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);

              return (
                <tr key={index} className={isWeekend(date) ? 'bg-gray-100' : ''}>
                  <td className="border px-4 py-2">
                    {`${date.getDate()}日(${dayOfWeek})`}
                  </td>
                  <td className="border px-4 py-2">
                    {shift.startTime && shift.endTime ? 
                      `${shift.startTime} - ${shift.endTime}` : 
                      '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.clock_in ? (
                      <div className="flex items-center">
                        <span>{record.clock_in}</span>
                        {inRecordId && (
                          <button 
                            onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="出勤時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.clock_out ? (
                      <div className="flex items-center">
                        <span>{record.clock_out}</span>
                        {outRecordId && (
                          <button 
                            onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="退勤時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.break_time ? (
                      <div className="flex items-center">
                        <span>{record.break_time}</span>
                        {breakTimeRecordId && (
                          <button 
                            onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="休憩時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.note ? (
                      <div className="flex items-center">
                        <span>{record.note}</span>
                        {noteRecordId && (
                          <button 
                            onClick={() => handleDeleteField(noteRecordId, 'note')}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="備考を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(record, dateStr)}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                      >
                        修正
                      </button>
                      <button
                        onClick={() => handleIrregularClick(dateStr)}
                        className="bg-gray-500 text-white px-2 py-1 rounded text-sm"
                      >
                        イレギュラー
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* モバイル表示 */}
      {isMobile && (
        <div className="space-y-4">
          {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
            const [year, month, day] = dateStr.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
            const shift = shifts[dateStr] || { startTime: '', endTime: '' };
            const record = records.find(r => r.date === dateStr) || {};
            
            // 出勤・退勤レコードのIDを取得
            const recordsForDate = record.records || [];
            const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
            const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
            const inRecordId = inRecord ? inRecord.id : null;
            const outRecordId = outRecord ? outRecord.id : null;
            
            // 休憩時間と備考のレコードIDを特定
            const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
            const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);

            return (
              <div 
                key={index} 
                className={`bg-white rounded-lg shadow-md p-4 ${isWeekend(date) ? 'bg-gray-50' : ''}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">
                    {`${date.getDate()}日(${dayOfWeek})`}
                  </h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditClick(record, dateStr)}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      修正
                    </button>
                    <button
                      onClick={() => handleIrregularClick(dateStr)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      イレギュラー
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">予定: </span>
                    <span className="font-medium">
                      {shift.startTime && shift.endTime ? 
                        `${shift.startTime} - ${shift.endTime}` : 
                        '---'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-600">出勤: </span>
                    <span className="font-medium ml-1">
                      {record.clock_in || '---'}
                    </span>
                    {record.clock_in && inRecordId && (
                      <button 
                        onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="出勤時間を削除"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-600">退勤: </span>
                    <span className="font-medium ml-1">
                      {record.clock_out || '---'}
                    </span>
                    {record.clock_out && outRecordId && (
                      <button 
                        onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="退勤時間を削除"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-600">休憩: </span>
                    <span className="font-medium ml-1">
                      {record.break_time || '---'}
                    </span>
                    {record.break_time && breakTimeRecordId && (
                      <button 
                        onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="休憩時間を削除"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {record.note && (
                  <div className="mt-2 text-sm flex items-center">
                    <span className="text-gray-600">備考: </span>
                    <span className="font-medium ml-1">
                      {record.note}
                    </span>
                    {noteRecordId && (
                      <button 
                        onClick={() => handleDeleteField(noteRecordId, 'note')}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="備考を削除"
                      >
                        ×
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 修正モーダル */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">打刻修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">出勤時刻</label>
              <input
                type="time"
                value={editData.clock_in}
                onChange={(e) => setEditData({...editData, clock_in: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">退勤時刻</label>
              <input
                type="time"
                value={editData.clock_out}
                onChange={(e) => setEditData({...editData, clock_out: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">休憩時間（時間）</label>
              <select
                value={editData.break_time || "1"}
                onChange={(e) => setEditData({...editData, break_time: e.target.value})}
                className="border rounded px-2 py-1"
              >
                <option value="0.5">0:30</option>
                <option value="1">1:00</option>
                <option value="1.5">1:30</option>
                <option value="2">2:00</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">修正理由 <span className="text-red-500">*</span></label>
              <textarea
                value={editData.modification_reason}
                onChange={(e) => setEditData({...editData, modification_reason: e.target.value})}
                className="w-full border rounded px-2 py-1"
                required
              />
            </div>
            <div>
              <label className="block mb-1">備考</label>
              <textarea
                value={editData.note}
                onChange={(e) => setEditData({...editData, note: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 border rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={!editData.modification_reason}
              className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
            >
              保存
            </button>
          </div>
        </div>
      </Modal>

      {/* 欠勤モーダル → イレギュラーモーダルに変更 */}
      <Modal isOpen={isAbsenceModalOpen} onClose={() => setIsAbsenceModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">イレギュラー登録</h2>
          
          <div className="mb-4">
            <label className="block mb-1">種別 <span className="text-red-500">*</span></label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="late"
                  checked={editData.irregularType === 'late'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                遅刻
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="early"
                  checked={editData.irregularType === 'early'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                早退
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="absence"
                  checked={editData.irregularType === 'absence'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                欠勤
              </label>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block mb-1">理由 <span className="text-red-500">*</span></label>
            <textarea
              value={editData.note}
              onChange={(e) => setEditData({...editData, note: e.target.value})}
              className="w-full border rounded px-2 py-1"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block mb-1">報告したクライアント責任者名</label>
            <input
              type="text"
              value={editData.reportedTo}
              onChange={(e) => setEditData({...editData, reportedTo: e.target.value})}
              className="w-full border rounded px-2 py-1"
              placeholder="例: 山田様"
            />
          </div>
          
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setIsAbsenceModalOpen(false)}
              className="px-4 py-2 border rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveAbsence}
              disabled={!editData.note}
              className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
            >
              保存
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AttendanceRecord;